import { useAuth0 } from '@auth0/auth0-react'
import { Ability, AbilityTuple, MongoQuery, RawRuleFrom } from '@casl/ability'
import axios from 'axios'
import { useEffect, useState } from 'react'
import useSWR, { SWRResponse } from 'swr'

const fetcher = async (accessToken: string, url: string) => {
  const api = axios.create({
    baseURL:
      process.env.NODE_ENV == 'production'
        ? '/api/'
        : 'http://127.0.0.1:8787/api/',
    headers: {
      authorization: `Bearer ${accessToken}`,
    },
  })

  return await api.get(url).then((r) => r.data)
}

export const useApi = () => {
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const { getAccessTokenSilently } = useAuth0()

  useEffect(() => {
    getAccessTokenSilently().then((token) => setAccessToken(token))
  }, [getAccessTokenSilently])

  if (!accessToken) {
    return {
      isLoading: true,
    }
  }

  const api = axios.create({
    baseURL:
      process.env.NODE_ENV == 'production'
        ? '/api/'
        : 'http://127.0.0.1:8787/api/',
    headers: {
      authorization: `Bearer ${accessToken}`,
      'content-type': 'application/json',
    },
  })

  return {
    isLoading: false,
    api,
  }
}

const useGetApi = <T>(url: string) => {
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const { getAccessTokenSilently } = useAuth0()

  useEffect(() => {
    getAccessTokenSilently().then((token) => setAccessToken(token))
  }, [getAccessTokenSilently])

  return useSWR<T>(accessToken ? [accessToken, url] : null, fetcher)
}

export const useAbility = () => {
  const { data } =
    useGetApi<RawRuleFrom<AbilityTuple, MongoQuery>[]>('/casl/abilities')

  return { ability: new Ability(data || []) }
}

export interface Entrance {
  ref: string
  name: string
}

export interface Sensor {
  ref: string
  serial: string
  assigned_entrance: string
  reversed: boolean
}

type RechartsEntry = { [key: string]: number | string; name: string }

export interface ReadingData {
  grouped: RechartsEntry[]
  averageFootfall: number
  percentChange: number
  barKeys: string[]
}

export interface Intervals {
  [key: string]: {
    [key: string]: string | number
    displayName: string
  }
}

export interface Groupings {
  [key: string]: {
    [key: string]: string | number
    displayName: string
  }
}

export const useGetEntrances = (): SWRResponse<Entrance[], unknown> =>
  useGetApi('/sensor_setup/entrances')

export const useGetSensors = (): SWRResponse<Sensor[], unknown> =>
  useGetApi('/sensor_setup/sensors')

export const useGetIntervals = (): SWRResponse<Intervals, unknown> =>
  useGetApi('/query/intervals')

export const useGetGroupings = (): SWRResponse<Groupings, unknown> =>
  useGetApi('/query/groupings')

export const useGetDefaults = (): SWRResponse<
  { defaultInterval: string; defaultGrouping: string },
  unknown
> => useGetApi('/query/defaults')

export const useLiveOccupancy = (): SWRResponse<
  { live_occupancy: number },
  unknown
> => useGetApi('/query/live_occupancy')

export const useQuery = (
  start: number,
  end: number,
  interval: string,
  grouping: string,
  entrance: string,
): SWRResponse<ReadingData, unknown> =>
  useGetApi(
    `/query/readings?start=${start}&end=${end}&interval=${interval}&grouping=${grouping}&entrance=${entrance}`,
  )

export const useGetSensorAuthKey = () =>
  useGetApi<{ auth_key: string }>('/sensor_setup/auth_key')
